import { Fragment, useEffect, useMemo } from 'react';
import { Link, useParams, useRouterState } from '@tanstack/react-router';
import { ChevronRightIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  readDocumentTitle,
  writeDocumentTitle,
} from '@/shared/utils/documentTitle';
import { opensOnBoard } from '@/features/workflows';
import { useQuery } from '@tanstack/react-query';
import { workspaceQueryOptions } from '@/features/workspaces';
import { crumbsFromPathname, type CrumbNames } from '../crumbs';
import { useBreadcrumbOverridesStore } from '../breadcrumbOverridesStore';

export function Breadcrumbs({ className }: { className?: string }) {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const openTaskId = useRouterState({
    select: (state) => {
      const task = (state.location.search as { task?: unknown }).task;
      return typeof task === 'string' ? task : undefined;
    },
  });
  const { workspaceId = '' } = useParams({ strict: false });
  // The same read the shell already makes for the workspace's name; the trail
  // names what it holds instead of printing ids (spec 128 R1).
  // Cached already by the shell; `enabled` so a page outside a workspace
  // asks for nothing.
  const { data: workspace } = useQuery({
    ...workspaceQueryOptions(workspaceId),
    enabled: workspaceId !== '',
  });
  const names = useMemo<CrumbNames | undefined>(() => {
    if (!workspace) return undefined;
    const base = `/workspaces/${workspaceId}/workflows`;
    const keyById = new Map(workspace.workflows.map((w) => [w.id, w.key]));
    return {
      workspace: workspace.name,
      workflows: new Map(
        workspace.workflows.map((w) => [
          w.key,
          {
            name: w.name,
            href: `${base}/${w.key}/${opensOnBoard(w) ? 'board' : 'builder'}`,
          },
        ]),
      ),
      tasks: new Map(
        workspace.tasks.map((t) => [
          t.id,
          {
            title: t.title,
            href: `${base}/${keyById.get(t.workflow_id) ?? ''}/board?task=${t.id}`,
          },
        ]),
      ),
    };
  }, [workspace, workspaceId]);
  // Only applied when they name *this* pathname (see the store's own
  // docstring) — a page that has since been left cannot still be shaping
  // the trail for wherever the reader navigated to.
  const { pathname: overridesFor, overrides } = useBreadcrumbOverridesStore();
  const crumbs = crumbsFromPathname(
    pathname,
    overridesFor === pathname ? (overrides ?? undefined) : undefined,
    names,
  );

  /*
    The browser tab says where it is (spec 128 R2): the page, then the
    workspace — and the open task first when a drawer names one. Every tab used
    to read the same.
  */
  const last = crumbs.at(-1)?.label;
  const pageTitle =
    overridesFor === pathname ? overrides?.titleLabel : undefined;
  const taskTitle = openTaskId
    ? names?.tasks.get(openTaskId)?.title
    : undefined;
  useEffect(() => {
    if (!last || !names?.workspace) return undefined;
    const previous = readDocumentTitle();
    writeDocumentTitle(
      [taskTitle, pageTitle ?? last, names.workspace]
        .filter((part, index, all) => part && all.indexOf(part) === index)
        .join(' · '),
    );
    return () => {
      writeDocumentTitle(previous);
    };
  }, [last, pageTitle, names?.workspace, taskTitle]);

  if (crumbs.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className={cn('min-w-0', className)}>
      <ol className="text-muted-foreground flex items-center gap-1.5 text-sm">
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          /*
            Below md only the page itself shows (spec 184). Spec 122 kept the
            last two behind an ellipsis, but beside the menu, search, bell and
            account a phone's top bar truncated both into "Hello Worl… › Bui…",
            and the ellipsis led nowhere. Hidden by class rather than sliced, so
            the markup still carries the whole trail.
          */
          const hidden = isLast ? undefined : 'max-md:hidden';
          return (
            <Fragment key={`${String(index)}:${crumb.href}`}>
              {index > 0 ? (
                <li
                  aria-hidden="true"
                  className="flex items-center max-md:hidden"
                >
                  <ChevronRightIcon className="size-3.5" strokeWidth={1.5} />
                </li>
              ) : null}
              {/*
                The text is a block box so `truncate` can end it in an
                ellipsis: on an inline box it clips nothing, and on a phone a
                long workspace name ran on under the search control (spec 151
                FR-008). The full name stays readable on hover.
              */}
              <li className={cn('min-w-0', hidden)}>
                {isLast ? (
                  <span
                    aria-current="page"
                    title={crumb.label}
                    className="text-foreground block truncate font-medium"
                  >
                    {crumb.label}
                  </span>
                ) : (
                  <Link
                    to={crumb.href}
                    title={crumb.label}
                    className="hover:text-foreground focus-visible:ring-ring block truncate rounded-sm transition-colors focus-visible:ring-3 focus-visible:outline-none"
                  >
                    {crumb.label}
                  </Link>
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
